import {Card, openConfirmDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil, mergeAll} from 'ramda';

import {useDeleteTaskMutation} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  type DmsDataGridProps,
  handleApiError,
  Main,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {type ActionCallback, type QueryFilterObject} from 'features/eag-grid';

import {TaskRelations} from '../../types/TaskRelations';
import {openCreateTaskFormDialog} from '../../utils/openCreateTaskFormDialog';
import {openEditTaskFormDialog} from '../../utils/openEditTaskFormDialog';
import {taskBulkOperationActionCallback} from '../../utils/taskBulkOperationActionCallback';

type SupportedRelations =
  | 'customer'
  | 'vehicle'
  | 'business-case'
  | 'interest'
  | 'service-case'
  | 'direct-sale'
  | 'tire-order'
  | 'workshop-interest';

type TaskGridCode = `${SupportedRelations}-task` | 'task';

function getTaskRelationByGridCode(gridCode: TaskGridCode) {
  return match<TaskGridCode, TaskRelations | undefined>(gridCode)
    .with('vehicle-task', () => EntityResourceIds.vehicle)
    .with('business-case-task', () => EntityResourceIds.businessCase)
    .with('customer-task', () => EntityResourceIds.customer)
    .with('interest-task', () => EntityResourceIds.interest)
    .with('service-case-task', () => EntityResourceIds.serviceCase)
    .with('direct-sale-task', () => EntityResourceIds.directSale)
    .with('tire-order-task', () => EntityResourceIds.tireOrder)
    .with('workshop-interest-task', () => EntityResourceIds.workshopInterest)
    .otherwise(() => undefined);
}

interface TaskDataGridProps extends RequiredTestIdProps, Pick<DmsDataGridProps, 'maximization'> {
  gridCode: TaskGridCode;
  entityId: string;
  isReadOnly?: boolean;
  autoHeight?: boolean;
}

export function TaskGridPage(props: TaskDataGridProps) {
  const [deleteTask] = useDeleteTaskMutation();
  const [canCreateTasks] = usePermissions({
    permissionKeys: ['createTask'],
  });

  const isAutoHeight = props.autoHeight !== false;
  const resourceId = getTaskRelationByGridCode(props.gridCode);
  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {relatedTo: props.entityId}]);

  const dataGridActionsHandler: ActionCallback = ({actionKey, rowId, refreshData}) => {
    match(actionKey)
      .with('detail', () => {
        if (Array.isArray(rowId)) {
          // This should never happen, but just in case
          throw new Error('Cannot view multiple tasks at once');
        }
        openEditTaskFormDialog({
          id: rowId,
          onEdit: refreshData,
          onDelete: refreshData,
          'data-testid': props['data-testid'],
        });
      })
      .with('edit', () => {
        if (Array.isArray(rowId)) {
          // This should never happen, but just in case
          throw new Error('Cannot edit multiple tasks at once');
        }
        openEditTaskFormDialog({
          id: rowId,
          onEdit: refreshData,
          onDelete: refreshData,
          'data-testid': props['data-testid'],
        });
      })
      .with('delete', () => {
        openConfirmDialog({
          onConfirm: () => {
            if (Array.isArray(rowId)) {
              // This should never happen, but just in case
              throw new Error('Cannot delete multiple tasks at once');
            }
            deleteTask({id: rowId})
              .unwrap()
              .then(() => {
                refreshData();
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
              })
              .catch(handleApiError);
          },
        });
      });
  };
  return (
    <Main isFullHeight data-testid={props['data-testid']}>
      <Card
        title={i18n.t('entity.task.labels.tasks')}
        isFullHeight={!isAutoHeight}
        actions={[
          {
            type: 'button',
            variant: 'primary',
            isDisabled: !canCreateTasks || isNil(resourceId) || props.isReadOnly,
            title: i18n.t('entity.task.actions.createTask'),
            onClick: () =>
              openCreateTaskFormDialog({
                'data-testid': suffixTestId('createTaskDialog', props),
                relatedRecordId: props.entityId,
                resourceId,
                onCreate: () => refreshDmsDatagrid(props.gridCode),
              }),
          },
        ]}
      >
        <DmsDataGrid
          maximization={props.maximization}
          data-testid={props['data-testid']}
          gridCode={props.gridCode}
          actionCallback={dataGridActionsHandler}
          bulkOperationActionCallback={taskBulkOperationActionCallback}
          autoHeight={isAutoHeight ? true : undefined}
          queryModifier={queryModifier}
          disableAllActions={props.isReadOnly}
          emptyState={{
            headline: i18n.t('general.labels.noData'),
          }}
        />
      </Card>
    </Main>
  );
}
